import type { QueryClient } from '@tanstack/react-query';

/**
 * 쿼리 키 팩토리에 **무효화 계약**을 붙인다.
 *
 * ## 무엇을 푸는가
 *
 * 뮤테이션이 `invalidateQueries({ queryKey: xxxKeys.all })` 한 줄로 도메인 전체를 지우면,
 * **무엇을 왜 지우는지가 코드에 남지 않는다.** 새 뮤테이션을 쓰는 사람은 앞 사람 줄을 복사할
 * 뿐이고 리뷰어도 diff 에서 판단할 근거가 없다. 이 계약은 각 훅이 **무엇을 무효화하는지
 * 선언**하게 만든다 — `언제·어떻게` 부를지는 훅이 그대로 정한다.
 *
 * ```ts
 * const raw = defineQueryKeys('checkpoints', (all) => ({ … }));
 *
 * export const {
 *   keys: checkpointKeys,
 *   root: checkpointRoot,
 *   invalidate: invalidateCheckpointScopes,
 * } = defineInvalidation(raw, {
 *   detail: raw.detailAll,
 *   batch: raw.batchAll,
 *   teamPendingReviews: raw.teamPendingReviewsAll,
 * });
 * ```
 *
 * ## 왜 함수인가 (뮤테이션 래퍼가 아니라)
 *
 * 호출 모양이 도메인마다 제각각이다 — `useCheckpointEditor` 는 4개 뮤테이션을 조합한 뒤
 * 마지막에 한 번, `useApproveCheckpoint` 는 다른 4개 도메인과 함께 부른다. 래퍼로 감싸면
 * 이런 모양이 안 들어간다(설계 노트 §4-1 이 래퍼를 기각한 이유와 같다).
 *
 * ## 강제 — 루트를 공개 표면에서 닫는다
 *
 * 반환하는 `keys` 에는 `all` 이 없다(`Omit`). 열어두면 새 뮤테이션이 계약을 무시하고 광역
 * 무효화를 적어도 컴파일이 통과하고, 그러면 계약이 권고로 전락한다. 루트가 필요한 곳
 * (계약 구현·회귀 가드)은 `root` 를 쓰되 **배럴로 내보내지 않는다.**
 *
 * ## 좁히기 도구가 아니다
 *
 * 커버리지를 줄이는 게 목적이 아니다. 체크포인트 도메인은 좁히기가 이미 한 번 부도났다
 * (PR #485 — proof 뮤테이션이 단일 퀘스트로 좁혀 배치 파생 뷰가 갱신되지 않았다). 기본은
 * 영향받는 scope 를 모두 선언하는 것이고, 빼려면 근거를 호출부 주석에 남긴다.
 *
 * `invalidateQueries` 기본 `refetchType` 은 `'active'` 라 **닫힌 화면은 refetch 0회**다 —
 * scope 를 넉넉히 선언하는 비용은 열린 화면 몇 개가 더 도는 정도다.
 */
export function defineInvalidation<
  TKeys extends { readonly all: readonly unknown[] },
  TScopes extends Record<string, readonly unknown[]>,
>(
  keys: TKeys,
  scopes: TScopes
): {
  /** 조회 키 + 무효화용 중간 접두사. **루트(`all`)는 없다.** */
  readonly keys: Omit<TKeys, 'all'>;
  /** 도메인 루트. 계약 구현·회귀 가드 전용 — 배럴로 내보내지 말 것. */
  readonly root: TKeys['all'];
  /** 선언한 scope 만 무효화한다. */
  readonly invalidate: (queryClient: QueryClient, declared: readonly (keyof TScopes)[]) => void;
  /**
   * `invalidate` 와 같되, 재조회가 모두 끝나면 resolve 한다. mutation 콜백이 이걸 return 하면
   * 그동안 isPending 이 유지된다 — 결재 중 행 잠금처럼 새 목록이 올 때까지 버튼을 막아야 할 때.
   */
  readonly invalidateAndWait: (
    queryClient: QueryClient,
    declared: readonly (keyof TScopes)[]
  ) => Promise<void>;
} {
  const invalidateAndWait = async (
    queryClient: QueryClient,
    declared: readonly (keyof TScopes)[]
  ): Promise<void> => {
    // 같은 scope 를 두 번 선언해도 재조회는 한 번이면 된다 — 호출부가 여러 조건을 합쳐
    // 배열을 만들 때 중복이 생기기 쉬우므로 여기서 흡수한다.
    await Promise.all(
      [...new Set(declared)].map((scope) =>
        queryClient.invalidateQueries({ queryKey: scopes[scope] })
      )
    );
  };

  return {
    // `all` 은 타입에서만 지운다 — 런타임 객체를 새로 만들면 팩토리와 참조가 갈려,
    // `keys.detailAll === root` 같은 동일성 비교가 조용히 깨진다.
    keys,
    root: keys.all,
    invalidate: (queryClient, declared) => {
      void invalidateAndWait(queryClient, declared);
    },
    invalidateAndWait,
  };
}
